import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { OtaLogo } from '@/features/properties/OtaLogo';
import { cn } from '@/shared/lib/cn';
import { classifyChannel, type BookingSpan } from './calendarHelpers';
import { OnboardingGuestLinkButton } from './OnboardingGuestLinkButton';

interface Props {
  span: BookingSpan;
  propertyId: string;
  /** Onboarding-Link gibt es nur für den Betrieb (Endpunkt Admin/Staff). */
  canOpenOnboarding: boolean;
  canReassign?: boolean;
  onReassignRequested?: () => void;
  variant: 'popover' | 'sheet';
}

/**
 * Die nächsten Schritte zu einer Buchung in der Belegung: öffnen, Apartment wechseln, Onboarding-
 * Link. Jede Aktion nur mit dem heutigen Recht; Eigentümer sehen allein „Buchung öffnen“.
 */
export function BookingDetailActions({ span, propertyId, variant, ...rights }: Props) {
  const sheet = variant === 'sheet';
  const base = sheet
    ? 'rounded-control flex h-[52px] w-full items-center justify-between px-[18px] text-[15px] font-bold'
    : 'rounded-pill flex h-[42px] items-center px-3.5 text-[13px] font-bold';
  const arrow = sheet && <ArrowRight size={16} aria-hidden />;
  return (
    <div className={cn('flex gap-2', sheet ? 'flex-col' : 'flex-wrap')}>
      <Link
        to="/bookings/$id"
        params={{ id: span.bookingId }}
        className={cn(base, sheet ? 'bg-ink text-white' : 'bg-forest text-white')}
      >
        Buchung öffnen
        {arrow}
      </Link>
      {rights.canReassign && rights.onReassignRequested && (
        <button
          type="button"
          onClick={rights.onReassignRequested}
          className={cn(
            base,
            sheet ? 'bg-paper text-ink' : 'border-forest text-forest border-[1.5px]',
          )}
        >
          Apartment wechseln
          {arrow}
        </button>
      )}
      {rights.canOpenOnboarding && (
        <OnboardingGuestLinkButton
          bookingId={span.bookingId}
          propertyId={propertyId}
          className={cn(base, sheet ? 'bg-paper text-ink' : 'bg-card-alt text-ink')}
        />
      )}
    </div>
  );
}

/** Kanalzeichen im Kopf der Details: Markenlogo für OTA, „M“ auf Amber für manuelle Buchungen. */
export function DetailsChannelMark({ span, size }: { span: BookingSpan; size: 30 | 34 }) {
  if (classifyChannel(span.channel).slug !== 'manual') {
    return <OtaLogo channel={span.channelCategory} size={size} />;
  }
  return (
    <span
      aria-hidden
      style={{ width: size, height: size }}
      className="bg-amber text-ink grid flex-none place-items-center rounded-sm text-[13px] font-extrabold"
    >
      M
    </span>
  );
}

/** Plan-Banner der Details: Bernstein, solange geplant; Siegellack, wenn der Plan Klärung braucht. */
export function PlanBanner({
  plan,
  className,
}: {
  plan: { text: string; attention: boolean };
  className: string;
}) {
  return (
    <span
      role={plan.attention ? 'alert' : undefined}
      className={cn(
        'rounded-control font-bold',
        plan.attention ? 'bg-sealwax-tint text-sealwax' : 'bg-amber-tint text-amber-ink',
        className,
      )}
    >
      {plan.text}
    </span>
  );
}
